import React, { useState } from 'react'

import { Box, CircularProgress, Typography, Button } from '@mui/material'

import {

    SmartToy as RobotIcon,

    Person as PersonIcon,

    Build as BuildIcon,

} from '@mui/icons-material'

import {

    MessageWrapper,

    MessageAvatar,

    MessageBubble,

    MessageTimestamp,

    TypingIndicator,

} from '../styles'

import { ChatMessage, ChatMode, ToolExecution } from '../types'

import { AgentMarkdownRenderer } from './AgentMarkdownRenderer'

import { AgentStreamingIndicator } from './AgentStreamingIndicator'

import { ToolExecutionCard } from './ToolExecutionCard'

import { AgentErrorBanner } from './AgentErrorBanner'

import { ArtifactCard } from './ArtifactCard'

import { FeedbackButtons } from './FeedbackButtons'

import { WaitingFormMessage } from './WaitingFormMessage'

import { URLElicitationCard } from './URLElicitationCard'

import { OutputCardRenderer } from './OutputCardRenderer'



interface ChatMessageItemProps {

    message: ChatMessage

    assistantLabel: string

    mode: ChatMode

    agentName?: string

    conversationId?: string

    onCancel?: () => void

    onPark?: () => void

    isParked?: boolean

    onWaitingFormSubmit?: (messageId: string, values: Record<string, unknown>) => void

    onWaitingFormCancel?: (messageId: string) => void

    onElicitationComplete?: (messageId: string) => void

    onRetryTool?: (messageId: string, tool: ToolExecution) => void

    onTimeoutRetry?: (suggestion: string) => void

}



const formatTime = (date: Date): string =>

    new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })



export const ChatMessageItem: React.FC<ChatMessageItemProps> = ({

    message,

    assistantLabel,

    mode,

    agentName,

    conversationId,

    onCancel,

    onPark,

    isParked,

    onWaitingFormSubmit,

    onWaitingFormCancel,

    onElicitationComplete,

    onRetryTool,

    onTimeoutRetry,

}) => {

    const isUser = message.role === 'user'

    const [allExpanded, setAllExpanded] = useState(false)

    const [isHovered, setIsHovered] = useState(false)



    if (message.messageType === 'output_card' && message.outputCard) {

        return (

            <MessageWrapper isUser={false}>

                <MessageAvatar isUser={false}>

                    <RobotIcon />

                </MessageAvatar>

                <Box sx={{ maxWidth: '80%', width: '100%' }}>

                    <OutputCardRenderer card={message.outputCard} />

                    <MessageTimestamp>

                        {assistantLabel} • {formatTime(message.timestamp)}

                    </MessageTimestamp>

                </Box>

            </MessageWrapper>

        )

    }



    const toolExecutions = message.toolExecutions ?? []

    const artifacts = message.artifacts ?? []

    const hasTools = mode === 'agent' && !isUser && toolExecutions.length > 0

    const hasArtifacts = mode === 'agent' && !isUser && artifacts.length > 0

    const showStreamingIndicator =

        mode === 'agent' &&

        !isUser &&

        !message.isAwaitingInput &&

        (message.isStreaming === true || hasTools)



    const completedCount = toolExecutions.filter(

        t => t.status === 'completed' || t.status === 'failed',

    ).length

    const totalCount = toolExecutions.length



    const feedbackAgentId =

        message.streamingMeta?.agentId || agentName || ''

    const showFeedback =

        mode === 'agent' &&

        !isUser &&

        !message.isStreaming &&

        !message.isAwaitingInput &&

        !!feedbackAgentId &&

        !!conversationId &&

        !!message.content



    return (

        <MessageWrapper

            isUser={isUser}

            isCollapsed={message.isCollapsed}

            isRouterBubble={message.isRouterBubble}

            onMouseEnter={() => setIsHovered(true)}

            onMouseLeave={() => setIsHovered(false)}

        >

            <MessageAvatar isUser={isUser}>

                {isUser ? <PersonIcon /> : <RobotIcon />}

            </MessageAvatar>



            <Box sx={{ maxWidth: '80%', width: '100%' }}>

                {message.streamingError && mode === 'agent' && !isUser && (

                    <AgentErrorBanner error={message.streamingError} />

                )}

                {message.timeoutInfo && mode === 'agent' && !isUser && (

                    <Box sx={{

                        mb: 1, p: 1.5, borderRadius: 1,

                        bgcolor: 'color-mix(in srgb, var(--warning-color, #f59e0b) 10%, transparent)',

                        border: '1px solid color-mix(in srgb, var(--warning-color, #f59e0b) 30%, transparent)',

                    }}>

                        <Typography variant="body2" sx={{ mb: 1 }}>

                            {message.timeoutInfo.message}

                        </Typography>

                        {message.timeoutInfo.suggestions.map((s, i) => (

                            <Button

                                key={i}

                                size="small"

                                variant="outlined"

                                sx={{ mr: 0.5, mb: 0.5, textTransform: 'none', fontSize: 11 }}

                                onClick={() => onTimeoutRetry?.(s)}

                            >

                                {s}

                            </Button>

                        ))}

                    </Box>

                )}



                {message.rationale && mode === 'agent' && !isUser && (

                    <Box sx={{

                        mb: 1, p: 1, borderRadius: 1,

                        bgcolor: 'var(--main-bg, #f0f4f8)',

                        borderLeft: '3px solid var(--main-accent, #8b5cf6)',

                    }}>

                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>

                            Reasoning{message.agentTier ? ` (${message.agentTier})` : ''}

                        </Typography>

                        <Typography variant="body2" sx={{ fontSize: 12, fontStyle: 'italic' }}>

                            {message.rationale}

                        </Typography>

                    </Box>

                )}



                {showStreamingIndicator && (

                    <AgentStreamingIndicator

                        meta={message.streamingMeta ?? null}

                        toolExecutions={toolExecutions}

                        elapsedMs={message.elapsedMs ?? 0}

                        isStreaming={message.isStreaming === true}

                        streamingProgress={message.streamingProgress}

                        streamingEstimate={message.streamingEstimate}

                        onCancel={message.isStreaming ? onCancel : undefined}

                        onPark={message.isStreaming ? onPark : undefined}

                        isParked={isParked}

                    />

                )}



                {message.isAwaitingInput && message.waitingForm && mode === 'agent' && (

                    <Box sx={{ mb: 1 }}>

                        {message.waitingForm.mode === 'url' && message.waitingForm.elicitations ? (

                            <URLElicitationCard

                                elicitations={message.waitingForm.elicitations}

                                toolName={message.waitingForm.tool_name}

                                onComplete={() => onElicitationComplete?.(message.id)}

                                onCancel={() => onWaitingFormCancel?.(message.id)}

                            />

                        ) : (

                            <WaitingFormMessage

                                data={message.waitingForm}

                                onSubmit={values => onWaitingFormSubmit?.(message.id, values)}

                                onCancel={() => onWaitingFormCancel?.(message.id)}

                            />

                        )}

                    </Box>

                )}



                {hasTools && !message.isStreaming && (

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>

                        <Box sx={{

                            display: 'flex',

                            alignItems: 'center',

                            justifyContent: 'space-between',

                            px: 0.5,

                            mb: 0.5,

                        }}>

                            <Box sx={{

                                display: 'flex',

                                alignItems: 'center',

                                gap: 0.5,

                                fontSize: 11,

                                color: 'var(--main-text-secondary, #64748b)',

                                fontWeight: 500,

                            }}>

                                <BuildIcon sx={{ fontSize: 14 }} />

                                {completedCount}/{totalCount} tool{totalCount !== 1 ? 's' : ''} completed

                            </Box>

                            <button

                                onClick={() => setAllExpanded(x => !x)}

                                aria-expanded={allExpanded}

                                aria-label={

                                    allExpanded

                                        ? 'Collapse all tools'

                                        : 'Expand all tools'

                                }

                                style={{

                                    background: 'none',

                                    border: 'none',

                                    cursor: 'pointer',

                                    fontSize: 10,

                                    color: 'var(--link-color, #3b82f6)',

                                    padding: '2px 4px',

                                }}

                            >

                                {allExpanded ? 'Collapse all' : 'Expand all'}

                            </button>

                        </Box>



                        {toolExecutions.map((tool, i) => (

                            <ToolExecutionCard

                                key={tool.call_id}

                                tool={tool}

                                index={i}

                                totalTools={totalCount}

                                expanded={allExpanded}

                                showTimeline={totalCount > 1}

                                onRetry={onRetryTool ? t => onRetryTool(message.id, t) : undefined}

                            />

                        ))}

                    </Box>

                )}



                {hasArtifacts && (

                    <Box sx={{ mb: 1 }}>

                        {artifacts.map(artifact => (

                            <ArtifactCard key={artifact.artifact_id} artifact={artifact} />

                        ))}

                    </Box>

                )}



                {message.extractedEntities?.message && mode === 'agent' && !isUser && (

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>

                        {message.extractedEntities.message}

                    </Typography>

                )}



                {isUser && message.images && message.images.length > 0 && (

                    <Box

                        sx={{

                            display: 'flex',

                            flexWrap: 'wrap',

                            gap: 0.5,

                            mb: message.content ? 0.5 : 0,

                            justifyContent: 'flex-end',

                        }}

                    >

                        {message.images.map((src, i) => (

                            <Box

                                key={`${message.id}-img-${i}`}

                                component="img"

                                src={src}

                                alt=""

                                sx={{

                                    width: 72,

                                    height: 72,

                                    objectFit: 'cover',

                                    borderRadius: 1,

                                    border: '1px solid var(--card-border, #e2e8f0)',

                                }}

                            />

                        ))}

                    </Box>

                )}



                {message.content && mode === 'agent' && !isUser ? (

                    <MessageBubble isUser={false} className="message-bubble">

                        <AgentMarkdownRenderer

                            content={message.content}

                            isStreaming={message.isStreaming}

                        />

                    </MessageBubble>

                ) : message.content ? (

                    <MessageBubble

                        isUser={isUser}

                        className="message-bubble"

                        dangerouslySetInnerHTML={{ __html: message.content }}

                    />

                ) : null}



                {!isUser && message.isStreaming && !message.content && !showStreamingIndicator && (

                    <MessageBubble isUser={false}>

                        <TypingIndicator>

                            <span />

                            <span />

                            <span />

                        </TypingIndicator>

                    </MessageBubble>

                )}



                <MessageTimestamp>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                        <span>

                            {isUser ? 'You' : assistantLabel} • {formatTime(message.timestamp)}

                        </span>

                        {message.isStreaming && (

                            <CircularProgress

                                size={10}

                                sx={{ color: 'var(--main-accent, #3b82f6)' }}

                            />

                        )}

                        {showFeedback && isHovered && (

                            <FeedbackButtons

                                agentId={feedbackAgentId}

                                conversationId={conversationId!}

                                messageId={message.id}

                                originalResponse={message.content}

                            />

                        )}

                    </Box>

                </MessageTimestamp>

            </Box>

        </MessageWrapper>

    )

}



export default ChatMessageItem


